import { ReactElement, useState, useCallback, useMemo, useEffect } from "react";
import { Card, Flex, Text, Grid, AgGridReact, AgGrid } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { LoadingCellRenderer } from "~/pages/Billing/components/LoadingCellRenderer";
import { PAGINATION } from "~/pages/Billing/constants";
import { useBillingDate } from "../contexts/BillingDateContext";
import { AllClientEntities } from "./AllClientEntities";
import { getColumnDefinitions } from "./columnDefinitions";
import { EntitiesGraphqlDataSource } from "./entitiesGraphqlDataSource";
import { NoRowsOverlay } from "./NoRowsOverlay";
import {
  entitiesGridWrapper,
  entitiesOverviewCard,
  entitiesOverviewHeader,
} from "./styles_vanilla.css";
import { getRowId, updateGridDataSource } from "./utils";

const sideBarDef: AgGrid.SideBarDef = {
  position: "right",
  toolPanels: [
    {
      id: "columns",
      labelDefault: "Columns",
      labelKey: "columns",
      iconKey: "columns",
      toolPanel: "agColumnsToolPanel",
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
        suppressColumnFilter: false,
        suppressColumnSelectAll: false,
        suppressColumnExpandAll: true,
      },
    },
  ],
};

const colDefs = getColumnDefinitions();

export const EntitiesOverview = (): ReactElement => {
  const { asOfDateString } = useBillingDate();
  const [gridApi, setGridApi] = useState<AgGrid.GridApi>();

  const dataSource = useMemo(() => {
    return new EntitiesGraphqlDataSource({}, undefined, asOfDateString);
  }, [asOfDateString]);

  useEffect(() => {
    updateGridDataSource(gridApi, dataSource);
  }, [gridApi, dataSource]);

  const handleOnGridReady = useCallback((event: AgGrid.GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  const handleOnFilterChanged = useCallback((event: AgGrid.FilterChangedEvent) => {
    event.api.hideOverlay();
  }, []);

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = useMemo(
    () => ({
      suppressCellFocus: true,
      rowHeight: 48,
      domLayout: "normal",
      statusBar: {
        statusPanels: [{ statusPanel: "agTotalRowCountComponent" }],
      },
      rowModelType: "serverSide",
      getRowId: getRowId,
      pagination: true,
      paginationPageSizeSelector: false,
      cacheBlockSize: PAGINATION.UNASSIGNED_ENTITIES_PAGE_SIZE,
      paginationPageSize: PAGINATION.UNASSIGNED_ENTITIES_PAGE_SIZE,
      onGridReady: handleOnGridReady,
      onFilterChanged: handleOnFilterChanged,
      defaultColDef: {
        flex: 1,
        menuTabs: ["generalMenuTab", "filterMenuTab"],
      },
      rowStyle: {
        backgroundColor: Colors.Warning200,
      },
      sideBar: sideBarDef,
      noRowsOverlayComponent: NoRowsOverlay,
    }),
    [handleOnGridReady, handleOnFilterChanged],
  );

  return (
    <>
      <Flex data-testid="unassigned-entities-table" flexGrow={1}>
        <Card className={entitiesOverviewCard}>
          <Flex
            justifyContent="space-between"
            alignItems="flex-start"
            className={entitiesOverviewHeader}
          >
            <Text variant="h3">Entities unassigned to a client</Text>
          </Flex>
          <div className={entitiesGridWrapper}>
            <Grid
              gridOptions={gridOptions}
              columnDefs={colDefs}
              loadingCellRenderer={LoadingCellRenderer}
            />
          </div>
        </Card>
      </Flex>
      <AllClientEntities />
    </>
  );
};
